import { useMemo } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Button } from "@similarweb/ui-components";
import { useTranslate } from "@similarweb/i18n-provider";

import SubscriptionRemoveButton from "app/pages/sales-intelligence/modules/signals-api-webhook/components/SubscriptionRemoveButton/SubscriptionRemoveButton";

import ListDropdownCell from "../components/ListDropdownCell/ListDropdownCell";
import URLInputCell from "../components/URLInputCell/URLInputCell";
import type { IDynamicListOption, ISavedSearchWebhookTableRow } from "../types/webhook";

type FieldChangeHandler = (index: number, field: "url" | "queryId", value: string) => void;

const useCreateColumnConfig = (
    rows: readonly ISavedSearchWebhookTableRow[],
    dynamicLists: readonly IDynamicListOption[],
    handleCellChange: FieldChangeHandler,
    handleTest: (row: ISavedSearchWebhookTableRow) => void,
    handleDelete: (row: ISavedSearchWebhookTableRow) => void,
    handleError: (error: string | null, index: number, field: "url") => void,
): ColumnsType<ISavedSearchWebhookTableRow> => {
    const { translate } = useTranslate();

    return useMemo((): ColumnsType<ISavedSearchWebhookTableRow> => {
        return [
            {
                dataIndex: "url",
                key: "url",
                title: translate("si.management.saved-search-webhooks.column.url", {
                    count: rows.length,
                }),
                className: "webhook-url-cell",
                render: (value: string, { index }: ISavedSearchWebhookTableRow) => (
                    <URLInputCell
                        value={value}
                        index={index}
                        onChange={handleCellChange}
                        onErrorChange={handleError}
                    />
                ),
            },
            {
                dataIndex: "queryId",
                key: "queryId",
                title: translate("si.management.saved-search-webhooks.column.dynamic-list"),
                className: "queryId-cell",
                render: (value: string, { index }: ISavedSearchWebhookTableRow) => (
                    <ListDropdownCell
                        value={value}
                        index={index}
                        options={dynamicLists}
                        onChange={handleCellChange}
                    />
                ),
            },
            {
                dataIndex: "id",
                key: "testCell",
                width: 104,
                render: (_, rowValue: ISavedSearchWebhookTableRow) => {
                    return (
                        <Button
                            type="flat"
                            onClick={() => handleTest(rowValue)}
                            dataAutomation="saved-search-webhooks-test-endpoint"
                            isDisabled={!!rowValue.errors || !rowValue.url}
                        >
                            {translate("si.management.saved-search-webhooks.test")}
                        </Button>
                    );
                },
            },
            {
                dataIndex: "id",
                key: "deleteCell",
                width: 40,
                className: "delete-cell",
                render: (_, rowValue: ISavedSearchWebhookTableRow) => {
                    return (
                        <SubscriptionRemoveButton
                            isDisabled={rowValue.isDeleteDisabled}
                            isSubscription={!!rowValue.id}
                            onRemove={() => handleDelete(rowValue)}
                        />
                    );
                },
            },
        ];
    }, [rows.length, dynamicLists, handleCellChange, handleTest, handleDelete, handleError, translate]);
};

export default useCreateColumnConfig;
